'use client'
import clsx from 'clsx'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

export type TooltipProps = {
  children?: React.ReactNode
  title?: string | React.ReactNode
  placement?: 'top' | 'top-start' | 'bottom' | 'left' | 'right' | 'right-start'
  className?: string
  arrow?: boolean
  open?: boolean
}

interface PositionType {
  top?: number
  bottom?: number
  left?: number
  right?: number
}

export const Tooltip: React.FC<TooltipProps> = ({
  children,
  title,
  placement = 'right',
  className = '',
  arrow = true,
  open,
}) => {
  const triggerRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState<PositionType>({})
  const [hovered, setHovered] = useState(false)

  const setTriggerRef = useCallback((node: HTMLDivElement | null) => {
    if (node) triggerRef.current = node
  }, [])

  useEffect(() => {
    if (!triggerRef.current) return
    if (!open && !hovered) return
    const rect = triggerRef.current.getBoundingClientRect()
    const scrollX = window.scrollX
    const scrollY = window.scrollY
    const offsets = {
      'top': {
        bottom: window?.innerHeight - rect.top + scrollY + rect.height / 2, // 8px gap
        left: rect.left + scrollX + rect.width / 2,
      },
      'top-start': {
        bottom: window?.innerHeight - rect.top + scrollY + rect.height / 2, // 8px gap
        left: rect.left + scrollX + 24,
      },
      'bottom': {
        top: rect.bottom + scrollY + 12,
        left: rect.left + scrollX + rect.width / 2,
      },
      'left': {
        top: rect.top + scrollY + rect.height / 2,
        right: window?.innerWidth - rect.left + scrollX + 12,
      },
      'right': {
        top: rect.top + scrollY + rect.height / 2,
        left: rect.right + scrollX + 12,
      },
      'right-start': {
        top: rect.top + scrollY + rect.height / 2,
        left: rect.left + scrollX + 36,
      },
    }
    setPosition(offsets[placement])
  }, [hovered, open, placement])

  const arrowRotationClass = {
    'top': 'rotate-180',
    'top-start': 'rotate-180',
    'bottom': '',
    'left': 'rotate-90',
    'right': '-rotate-90',
    'right-start': '-rotate-90',
  }[placement]

  const arrowPositionClass = {
    'top': 'mb-[-0.5rem]',
    'top-start': 'mb-[-0.5rem]',
    'bottom': 'mt-[-0.5rem]',
    'left': 'mr-[-1rem]',
    'right': 'ml-[-1rem]',
    'right-start': 'ml-[-1rem]',
  }[placement]

  const arrowContainerClass = {
    'top': 'absolute bottom-0 left-1/2 -translate-x-1/2',
    'top-start': 'absolute right-0 bottom-0',
    'bottom': 'absolute top-0 left-1/2 -translate-x-1/2',
    'left': 'absolute right-0 top-1/2 -translate-y-1/2',
    'right': 'absolute left-0 top-1/2 -translate-y-1/2',
    'right-start': 'absolute left-0 top-1/2 -translate-y-1/2',
  }[placement]

  const transformFix = {
    'top': 'translate-x-[-50%]',
    'top-start': 'translate-x-[-100%]',
    'bottom': 'translate-x-[-50%]',
    'left': 'translate-y-[-50%]',
    'right': 'translate-y-[-50%]',
    'right-start': 'translate-y-[-50%]',
  }[placement]

  const bgColors = 'bg-teal-700 dark:bg-teal-700'

  const tooltipEl = (
    <div
      className={clsx(
        'z-[1001] pointer-events-none absolute min-w-max transition-all duration-300',
        (open || hovered) ? 'scale-100 opacity-100' : 'scale-0 opacity-0',
        transformFix,
      )}
      style={{
        position: 'absolute',
        ...position,
      }}
    >
      <div
        style={{ zIndex: 101 }}
        className={clsx(
          'relative pointer-events-none rounded',
          'px-3 py-2 text-center text-sm shadow-lg max-w-sm',
          'text-white',
          bgColors,
          className
        )}
      >
        {arrow && (
          <div
            className={clsx(
              'h-4 w-8 clip-bottom',
              bgColors,
              arrowRotationClass,
              arrowPositionClass,
              arrowContainerClass
            )}
          />
        )}
        {title}
      </div>
    </div>
  )

  const hasPosition = Object.keys(position).length > 0
  const tooltipRoot =
    typeof window !== 'undefined'
      ? document.getElementById('tooltip-root')
      : null

  return (
    <div
      ref={setTriggerRef}
      className=""
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <style>{`.clip-bottom { clip-path: polygon(50% 0, 100% 100%, 0 100%); }`}</style> {/* clip the arrow element into a triangle */}
      {children}
      {tooltipRoot && hasPosition && createPortal(tooltipEl, tooltipRoot)}
    </div>
  )
}
